import React from 'react'
import {
  Anchor,
  Button,
  Flex,
  PinInput,
  Spacer,
  Text,
  Transition,
} from '@sendoutcards/quantum-design-ui'
import { useActions, useQueryParams, useState } from 'src/hooks'
import {
  useUnlockAccountSendCode,
  useUnlockAccountVerifyCode,
} from 'src/react_query/mutations/hooks'
import { UnlockAccountVerifyCodeStatusCode } from 'src/graphql/generated/graphql'

type Props = {
  username: string
  phoneNumber?: string
}

export const UnlockVerifyCode = (props: Props) => {
  const { username, phoneNumber } = props
  const [verificationCode, setVerificationCode] = useState('')
  const [failureMessage, setFailureMessage] = useState<string>()
  const verifyCodeMutation = useUnlockAccountVerifyCode()
  const unlockMutation = useUnlockAccountSendCode()
  const actions = useActions()
  const params = useQueryParams()
  const { sentTo } = params

  const isVerifying = verifyCodeMutation.isLoading || unlockMutation.isLoading

  const handleVerify = (code: string) => {
    if (code.length !== 6) {
      return setFailureMessage('Verification code must be 6 digits.')
    }

    setFailureMessage(undefined)

    verifyCodeMutation.mutate(
      { username, code },
      {
        onSuccess: data => {
          const result = data.unlockAccountVerifyCode

          if (
            result.statusCode === UnlockAccountVerifyCodeStatusCode.Success &&
            result?.token
          ) {
            actions.saveToken(result.token)
            window.location.href = `/dashboard/account`
          } else {
            setFailureMessage(result.message ?? 'Failed to verify code')
          }
        },
        onError: (error: unknown) => {
          setFailureMessage(
            error instanceof Error
              ? error.message
              : 'Failed to verify code. Please try again.',
          )
        },
      },
    )
  }

  const handleResendCode = () => {
    setFailureMessage(undefined)
    unlockMutation.mutate(
      { username },
      {
        onSuccess: data => {
          if (data.unlockAccountSendCode.success) {
            setFailureMessage(undefined)
          } else {
            setFailureMessage(
              data.unlockAccountSendCode.message || 'Failed to resend code.',
            )
          }
        },
        onError: () => {
          setFailureMessage('Failed to resend code. Please try again.')
        },
      },
    )
  }

  if (isVerifying) {
    return <Transition isLoading={true} title="Verifying code..." />
  }

  return (
    <Flex
      flexDirection="column"
      alignItems="center"
      style={{ maxWidth: '500px', width: '100%' }}
    >
      <Spacer space="x4" />
      <Text type="title" alignment="center" weight="bold">
        Enter Verification Code
      </Text>
      <Spacer space="x3" />
      <Text type="body" alignment="center">
        We've sent a 6-digit verification code to your registered phone number{' '}
        {sentTo}
      </Text>
      {phoneNumber && (
        <>
          <Spacer space="x2" />
          <Text type="body" weight="semiBold" alignment="center">
            {phoneNumber}
          </Text>
        </>
      )}
      <Spacer space="x4" />
      <form
        onSubmit={event => {
          event.preventDefault()
          handleVerify(verificationCode)
        }}
        style={{ width: '100%' }}
      >
        <Flex
          flexDirection="column"
          alignItems="center"
          style={{ width: '100%' }}
        >
          {failureMessage && (
            <>
              <Flex
                flexDirection="column"
                alignItems="center"
                style={{
                  width: '100%',
                  padding: '16px',
                  backgroundColor: '#FEE2E2',
                  borderRadius: '8px',
                  border: '1px solid #FCA5A5',
                  marginBottom: '24px',
                }}
              >
                <Text type="body" color="danger" alignment="center">
                  {failureMessage}
                </Text>
              </Flex>
            </>
          )}
          <PinInput
            length={6}
            hasFocus={true}
            onChange={setVerificationCode}
            onComplete={handleVerify}
          />
          <Spacer space="x4" />
          <Button fullWidth size="large">
            Verify Code
          </Button>
          <Spacer space="x3" />
          <Text type="caption" alignment="center">
            Didn't receive the code?
          </Text>
          <Spacer space="x1" />
          <Anchor
            title="Resend Code"
            isDecorated={true}
            onClick={handleResendCode}
          />
        </Flex>
      </form>
    </Flex>
  )
}
